import React, { createContext, useContext, useEffect, useState } from "react";
import { useInventoryContext } from "./InventoryContext";
import { usePrinterContext } from "./PrinterContext";
import { useShipmentContext } from "./ShipmentContext";
import { API } from "@/lib/api";
import { useLocation, useSearch } from "wouter";
import { useErrorModalContext } from "./ErrorModalContext";
import { resyncAllSalesData, resyncSellerData } from "@/api/sync";
import { getTeams } from "@/api/teams";
import { AssistantResponse, AssistantStatus, PaymentStatus, SettingsReponse, ShippingTemplate, SyncJobStatus, UserRole, UserStatus } from "@boxem/core-api-schema";
import { endSession, getMe, startSession } from "@/api/auth";
import { useSubscriptionContext } from "./SubscriptionContext";
import {useBuyCostContext} from "@/contexts/BuyCostContext";
import { LocalStorageKeys } from "@/utils/localStorageUtils";
import ConfirmModal, { ConfirmModalProps } from "@/components/ConfirmModal/ConfirmModal";
import Flex from "@/components/Flex/Flex";
import Button from "@/components/forms/Button/Button";
import { useIntercom } from "react-use-intercom";
import IconBilling from "@/components/icons/IconBilling";
import IconRequest from "@/components/icons/IconRequest";

export type Address = {
  id?: number;
  name: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  country?: string;
  zipcode: string;
  phone?: string;
  email?: string;
};

export enum UserGroup {
  Admin = "admin",
  User = "user",
}

export type Seller = {
  uuid: string;
  name: string;
  email: string;
  role: string;
  sellerId?: string;
  recurlyAccountCode: string;
  paymentStatus: PaymentStatus;
}

export type User = {
  //id: number; // we don't store/receive the sequential user id on the frontend.
  uuid: string;
  name: string;
  email: string;
  emailVerified?: boolean;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  status: UserStatus;
  role: UserRole;
  sellerId?: string; // if null, seller account is not connected.
  isActive: boolean;
  activeMarketplaceId?: string;
  isSubscribed: boolean;
  usersToGroups: UserGroup[]; // e.g., "admin", "user", etc.
  addresses: Address[];
  shippingTemplates: ShippingTemplate[];

  /*listingStatusSubscriptionId: string | null;
  listingIssuesSubscriptionId: string | null;

  itemInventoryChangeSubscriptionId: string | null;

  fulfillmentOrderStatusSubscriptionId: string | null;
  fbaInventoryAvailabilitySubscriptionId: string | null;
  fbaOutboundShipmentStatusSubscriptionId: string | null;
  feedProcessingFinishedSubscriptionId: string | null;
  reportProcessingFinishedSubscriptionId: string | null;

  syncJobStatus: string | null;
  syncJobId: string | null;
  boxemSyncJobId: string | null; */

  listingSyncStatus?: SyncJobStatus;
  shipmentSyncStatus?: SyncJobStatus;
  allSalesSyncStatus?: SyncJobStatus;

  recurlyAccountCode: string;
  paymentStatus: PaymentStatus;

  // preferences/settings:
  settings: SettingsReponse;
  permissions?: number;
  sellers: User[];
  goAuraIntegration: boolean;
  sellerSnapIntegration: boolean;
  bqoolIntegration: boolean;
};

const UserContext = createContext(undefined);
export interface TeamMember {
  relationId?: number;
  uuid?: string;
  email: string;
  name: string;
  role?: UserRole;
  permissions: number;
  emailVerified?: boolean;
  status?: AssistantStatus;
}

export const useUserContext = () => useContext<{
  ownerUUID: string; // this is the uuid of the site owner
  seller: User | undefined; // this is the current seller of the site
  user: User | undefined;  // this is the logged in user (could be admin/assistant/support/)
  addressBook: Address[]; // a merged list of the seller's addresses + the logged-in user's addresses. you should use this instead of seller/user.addresses
  hasTriedToGetUserFromLocalStorage: boolean;

  // pass 'refetchBatchesAndInventory' if you want to initiate a reload of all the seller's 
  // inventory and shipment data. note it does not refetch sales data.
  setUser: (user: Partial<User> | undefined, refetchBatchesAndInventory: boolean) => void;
  setSeller: (seller: User | undefined, refetchBatchesAndInventory?: boolean) => void;

  refetchUserData: () => void;

  pushNewAddress: (address: Address) => void;
  updateAddress: (address: Address) => void;

  resyncSellerData: () => Promise<void>;
  resyncAllSalesData: () => Promise<void>;

  teamLoading: boolean;
  fetchTeam: () => void;
  team: TeamMember[] | undefined;
  setTeam: (team: TeamMember[]) => void;

  logout: () => void;

}>(UserContext);

export const UserContextProvider = (props: { children: React.ReactNode }) => {
  const searchParams = new URLSearchParams(useSearch());
  const { refetchWholeInventory, clearInventory } = useInventoryContext();
  const { fetchCategories } = useBuyCostContext()
  const { initiateConnectionToQzTray } = usePrinterContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const { refetchSubscription } = useSubscriptionContext();
  const intercom = useIntercom();
  const [_, navigate] = useLocation();

  const [hasTriedToGetUserFromLocalStorage, setHasTriedToGetUserFromLocalStorage] = useState<boolean>(false);
  const [user, setUser] = useState<User | undefined>();
  const [seller, setSeller] = useState<User | undefined>();
  const [addressBook, setAddressBook] = useState<Address[]>([]);
  const [userProblemModal, setUserProblemModal] = useState<ConfirmModalProps>(null);

  // @TODO we need a "login as" logic so we can get the right owner UUID
  const [ownerUUID, setOwnerUUID] = useState(user?.uuid);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState<boolean>(false);

  const logout = () => {
    clearInventory();
    navigate("~/auth/login");
    endSession();
    API.logout();
  };

  const reFetchTeam = (ownerUUID: string) => {
    if (!!ownerUUID) {
      setTeamLoading(true);
      getTeams(ownerUUID)
        .then((data: AssistantResponse) => {
          if (data?.assistants.length > 0) {
            setTeam(data.assistants)
          }
        })
        .finally(() => setTeamLoading(false));
    }
  };

  const refetchUserData = () => {
    refetchSubscription();
    refetchWholeInventory();
    fetchCategories();
  };

  const pushNewAddress = (address: Address) => {
    user.addresses.push(address);
    window.localStorage.setItem(LocalStorageKeys.user, JSON.stringify(user));
    setUser({...user});
  };

  const updateAddress = (address: Address) => {
    const index = user.addresses.findIndex(a => a.id === address.id);
    if (index !== -1) {
      user.addresses[index] = address;
      window.localStorage.setItem(LocalStorageKeys.user, JSON.stringify(user));
      setUser({...user});
    }
  };

  const handleUserInitOrUpdate = (user: any, shouldRefetchUserData: boolean, connectToQzTray: boolean) => {
    if (user) {
      try {
        // save user in local storage
        window.localStorage.setItem(LocalStorageKeys.user, JSON.stringify(user));
        const storedSeller = window.localStorage.getItem(LocalStorageKeys.seller);
        let currSeller : User = storedSeller ? JSON.parse(storedSeller) : null;

        if (user.role !== UserRole.SELLER && user.role !== UserRole.ADMIN && user.role !== UserRole.SUPPORT) {
          if (currSeller) {
            const targetSeller = user.sellers.find((s) => s.uuid === currSeller.uuid);
            if (!targetSeller) {
              // the current seller has been removed
              currSeller = user.sellers[0];
            } else {
              currSeller = targetSeller;
            }
          } else {
            currSeller = user.sellers[0];
          }
        } else {
          currSeller = user;
        }
        window.localStorage.setItem(LocalStorageKeys.seller, JSON.stringify(currSeller));

        // @TODO(nick) temporarily disabling this because in the new sign-up flow, you get to the payment page
        // with UserStatus == NORMAL
        if (false && !currSeller.isSubscribed && currSeller.status !== UserStatus.INCOMPLETE && currSeller.status !== UserStatus.EMAIL_VERIFICATION) {
          setUserProblemModal({
            title: "Billing/Subscription Issue",
            open: true,
            onClose: () => setUserProblemModal(null),
            hideFooter: true,
            body: (
              <Flex direction="column" gap={4}>
                <p>It looks like your account is no longer subscribed.</p>

                <Flex gap={2}>
                  <Button
                    size="large"
                    type="primary"
                    onClick={() => {
                      // @NOTE(nick) this url will be wrong for staging/dev, the url in that case is:
                      // 'https://boxem-sandbox.recurly.com/account'
                      window.open("https://boxem.recurly.com/account", "_blank").focus();
                    }}
                  >
                    <IconBilling />
                    Manage Subscription
                  </Button>

                  <Button
                    type="secondary"
                    size="large"
                    onClick={() => {
                      sessionStorage.setItem("show-intercom", "true");
                      try {
                        intercom.update({ name: user?.name, email: user?.email, hideDefaultLauncher: false });
                        intercom.show();
                      } catch (err) {
                        console.error(err);
                      }
                      setUserProblemModal(null);
                    }}
                  >
                    <IconRequest />
                    Open Support Chat
                  </Button>
                </Flex>
              </Flex>
            )
          })
        }
        setSeller(currSeller);
        
        setOwnerUUID(currSeller.uuid);
        if (user.status !== UserStatus.INCOMPLETE && user.role !== UserRole.ADMIN && user.role !== UserRole.SUPPORT && currSeller.isSubscribed) {
          reFetchTeam(currSeller.uuid);

          if (shouldRefetchUserData) {
            refetchUserData();
          }
        }

        startSession();
      } catch (err) {
        console.log(err);
        logout();
      }
    } else {
      // user was set to null, just clear everything
      clearInventory();
      window.localStorage.removeItem(LocalStorageKeys.user); // this should've already been removed, but being safe...
    }

    // attempt to connect to qz-tray
    if (connectToQzTray) initiateConnectionToQzTray();
  };

  const handleSession = () => {
    if (!!user) {
      if (document.hidden) {
        endSession();
      } else {
        startSession();
      }
    }
  };

  useEffect(() => {

    //This is here to prevent a race condition from reverting the user state after we've successfully mirrored a users account.
    const mirrorToken = searchParams.get("mt"); // "mt" -> Mirror Token
    if (!mirrorToken) {
      const storedUser = window.localStorage.getItem(LocalStorageKeys.user);
  
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
        } catch (e) {
          console.error(e);
          // if we crash trying to get the user from local storage, local storage is probably corrupt.
          // force a re-login.
          logout();
        }
      }
  
      setHasTriedToGetUserFromLocalStorage(true);

      try {
        getMe().then((user: any /*UserResponse*/) => { // @TODO fix the 'any' here...
          setUser(user);
          handleUserInitOrUpdate(user, true, true);
          document.addEventListener("visibilitychange", handleSession);
        });
      } catch (err) {
        console.error(err);
        logout();
      }
    }

    //If context is unmounted (tab closed or inactive) we will end the user session.
    return () => {
      document.removeEventListener("visibilitychange", handleSession);
    };
  }, []);

  useEffect(() => {
    if (seller) {
      window.localStorage.setItem(LocalStorageKeys.seller, JSON.stringify(seller));
    }
  }, [seller]);

  useEffect(() => {
    const cmp = (a: Address, b: Address) => a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    if (user?.sellerId !== seller?.sellerId) {
      setAddressBook([...(user?.addresses) ?? [], ...(seller?.addresses ?? [])].sort(cmp));
    } else {
      // if the user and the seller are the same, they will both contain the same addresses.
      // avoid ending up with 2x the total count of addresses we should have:
      setAddressBook([...(user?.addresses) ?? []].sort(cmp));
    }
  }, [user, seller]);

  return (
    <UserContext.Provider value={{
      ownerUUID,
      user,
      setUser: (user: User, shouldRefetchUserData: boolean) => {
        setUser({...user});
        handleUserInitOrUpdate(user, shouldRefetchUserData, false);
      },
      seller,
      setSeller: (seller: User, shouldRefetchUserData: boolean = false) => {
        setSeller({...seller});
        if (shouldRefetchUserData && user.status !== UserStatus.INCOMPLETE && user.role !== UserRole.ADMIN && user.role !== UserRole.SUPPORT && seller.isSubscribed && seller.sellerId) {
          refetchUserData();
        }
      },
      addressBook,
      hasTriedToGetUserFromLocalStorage,
      pushNewAddress,
      updateAddress,
      logout,
      resyncSellerData: () => {
        return resyncSellerData()
          .then(result => {
            setSeller({ ...seller, ...result });
          })
          .catch(errors => {
            if (handleErrorResponseGenerically(errors)) {
              return;
            }
          });
      },
      resyncAllSalesData: () => {
        return resyncAllSalesData()
          .then(result => {
            setSeller({ ...seller, ...result });
          })
          .catch(errors => {
            if (handleErrorResponseGenerically(errors)) {
              return;
            }
          });
      },
      team,
      setTeam: (team: TeamMember[]) => {
        setTeam(team);
      },
      teamLoading,
    }}>
      <ConfirmModal
        title={userProblemModal?.title}
        open={userProblemModal?.open}
        onClose={userProblemModal?.onClose}
        body={userProblemModal?.body}
        hideFooter={userProblemModal?.hideFooter}
      />
      {props.children}
    </UserContext.Provider>
  );
};
